import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { GridPagerSettings } from '@progress/kendo-react-grid'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { isGroupedFilters } from 'utils/QueryHelpers'
import { v4 as uuid } from 'uuid'

import { FilterDescriptor, GridFilterActionState, GridFilterState } from './types'

export const pageableSettings: GridPagerSettings & { pageSizes: number[] } = {
  info: true,
  type: 'numeric',
  pageSizes: [10, 20, 50, 100],
}

export const initialState = (): GridFilterState => {
  const sessionPageSize = sessionStorage.getItem('itemsPerPage')
  const [pageSize] = pageableSettings.pageSizes

  return {
    skip: 0,
    take: sessionPageSize ? parseInt(sessionPageSize) : 10,
    sort: [],
    group: [],
    filterName: '',
    filter: {
      logic: 'or',
      filters: [],
    },
    total: 0,
    pageIndex: 1,
    pageSize: sessionPageSize ? parseInt(sessionPageSize) : pageSize,
  }
}

export const gridSlice = createSlice({
  name: 'grid',
  initialState: initialState(),
  reducers: {
    setGridFilters: (state: GridFilterState, action: PayloadAction<GridFilterActionState>) => {
      const gridState = {
        ...state,
        ...action.payload,
      }

      return {
        ...gridState,
        filter: setReduceAllGridFilters(gridState.filter),
      }
    },
    addGridFiltersByFilterId: (state: GridFilterState, action: PayloadAction<FilterDescriptor>) => {
      const gridState = {
        ...state,
        filter: addReduceGridFiltersByFilterId(state.filter, action),
      }
      return gridState
    },
    resetGridFilters: () => initialState(),
  },
})

const setReduceAllGridFilters = (
  filterDescriptor: CompositeFilterDescriptor
): CompositeFilterDescriptor => {
  return {
    logic: filterDescriptor.logic,
    filters: filterDescriptor.filters.map((filter) => {
      if (isGroupedFilters(filter)) {
        return setReduceAllGridFilters(filter)
      }
      return {
        ...filter,
        id: uuid(),
      }
    }),
  }
}

const addReduceGridFiltersByFilterId = (
  filterDescriptor: CompositeFilterDescriptor,
  action: PayloadAction<FilterDescriptor>
): CompositeFilterDescriptor => {
  return {
    logic: filterDescriptor.logic,
    filters: filterDescriptor.filters.map((filter) => {
      if (isGroupedFilters(filter)) {
        return addReduceGridFiltersByFilterId(filter, action)
      }

      const filterItem = filter as FilterDescriptor

      return filterItem?.id === action.payload.id
        ? {
            ...filterItem,
            ...action.payload,
          }
        : filterItem
    }),
  }
}

export const gridReducer = gridSlice.reducer
export const { setGridFilters, resetGridFilters, addGridFiltersByFilterId } = gridSlice.actions
